import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,

  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { Nodo, NodoPagingRes } from '@/shared/interfaces/erp/mantenimiento-operaciones';
import { isAxiosError } from 'axios';

const { get, post, put } = erpApi();

///* tanSctack query

export const useFetchNodos = ({
  page = 1,
  page_size = 10,
  nombre = '',
  ciudad = '',
}: GetNodoParams) => {
  return useQuery({
    queryKey: ['Nodo', page, nombre, ciudad, page_size], // Include ciudad in the query key
    queryFn: async () => {
      return getNodos({ page, nombre, ciudad, page_size }); // Pass ciudad to getNodo
    },
  });
};

export const useFetchAllNodes = ({
  enabled,
  params,
}: UseFetchEnabledParams<GetNodoParams> = {}) => {
  return useQuery({
    queryKey: ['Nodo', ...Object.values(params ?? {})],
    queryFn: () => getNodos(params),
    enabled,
  });
};

export const useGetNodo = (id: string) => {
  return useQuery({
    queryKey: ['Nodo', id],
    queryFn: async () => {
      return getNodo(id);
    },
    gcTime:1,
  });
};

export const useCreateNodo = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createNodo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Nodo'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Nodo creado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateNodo = ({
  navigate,
  enableRedirect = true,
  returnUrl,
  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,
  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateNodo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zones'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Nodo actualizado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetNodoParams = Partial<Nodo> & {
  page?: number;
  page_size?: number;

  es_radiobase?: boolean;
  filterByState?: boolean;
};

export type CreateNodoParams = Omit<Nodo, 'id_nodo'>;
export type UpdateNodoParams = {
  id: string;
  data: CreateNodoParams;
};

export const getNodos = async (params?: GetNodoParams) => {
  const stateParams = { ...params };

  // filterByState
  // if (stateParams.filterByState === false && stateParams.estado === undefined) {
  //   delete stateParams.estado;
  // } else if (stateParams.filterByState !== false) {
  //   stateParams.estado = true;
  // }
  // delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<NodoPagingRes>(`${urlBackend}api/v1/nodo/?${queryParams}`, true);
};

export const getNodo = async (id: string) => {
  try {
    return await get<Nodo>(`${urlBackend}api/v1/nodo/${id}/`, true);
  }
  catch (error) {
    return handleAxiosError(error);
  }
};

export const createNodo = async (data: CreateNodoParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<Nodo>(`${urlBackend}api/v1/nodo/`, data, true);

  setAlert(true, res.message, res.status);
  return res;
};

export const updateNodo = async ({ id, data }: UpdateNodoParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<Nodo>(`${urlBackend}api/v1/nodo/${id}/`, data, true);
  setAlert(true, res.message, res.status);
  return res;
};

/***
 * 
 * Importar Excel nodo
 * 
*/
export const useFetchImportarExcelNodo = (
  { enableRedirect, navigate, returnUrl }: UseMutationsParams
) => {
  const queryClient = useQueryClient();

  const setAlert = useUiStore.getState().setAlert;
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;


  return useMutation({

    mutationFn: importUploadExcelNodo,
    mutationKey: ['Nodo'],
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['radiobase-create-array'] });

      if (data.code === 200)
        enableRedirect && navigate && returnUrl && navigate(returnUrl);

      setAlert(true, data.message, data.status);
    },
    onError: (error => {
      if (isAxiosError(error)) {
        const respAxiosData = error.response?.data || {};
        if (respAxiosData.code == 400)
          return setAlert(true, `Se ha detectado un código duplicado. Por favor, corrige el archivo y vuelve a subirlo.`, AlertSeverityEnum.error)
      }
      handleAxiosError(error);
    }),
    onSettled: () => {
      setIsGlobalLoading(false);
    }
  });
};

export const importUploadExcelNodo = async (data: { nodos: any[] }) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true)
  const res = await post<any>(`${urlBackend}api/v1/nodo-create-array/`, data, true);
  return res;
};